import { Injectable, NgZone } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { CoreLogService } from "../../core/services/log.service";

declare global {
  interface Window { SOTWebSocketService: any; }
}

@Injectable()
export class SOTWebSocketService {
  private _port: number = 50100; // default port
  /** port for connection */
  public get port(): number {
    return this._port;
  }
  /** port for connection */
  public set port(port: number) {
    this._port = port;
  }

  /** WebSocket to POS */
  private _ws: WebSocket = null;

  /** status of connection */
  public get isConnected(): boolean {
    return (this._ws && this._ws.readyState == WebSocket.OPEN);
  }

  private _connectionStatusChanged: Subject<boolean> = new Subject<boolean>();
  /** firesif the connection status changed (true = connected, false disconnected) */
  public get connectionStatusChanged(): Observable<boolean> {
    return this._connectionStatusChanged.asObservable();
  }

  constructor(private _ngZone: NgZone,
    private _coreLogService: CoreLogService) {

    const me = this;
    window.SOTWebSocketService = window.SOTWebSocketService || {};

    window.SOTWebSocketService["OpenConnection"] = () => {
      me._ngZone.run(async () => {
        await me.OpenConnection();
      });
      return;
    };

    window.SOTWebSocketService["Send"] = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
      me.Send(data);
      return;
    };
  }

  /** try open connection */
  public OpenConnection(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>(async (resolve, reject) => {
      if (me.isConnected) {
        resolve(true);
        return;
      }

      // try close if old connection is not open
      if (me._ws) {
        try {
          me._ws.close();
        } catch (error) {
          me._coreLogService.error("SOTWebSocketService.OpenConnection", "ws.close()", error);
        }
      }
      // try open connection
      try {
        me._ws = await me.GetWebSocket();
        if (me._ws) {
          me._ws.onerror = (event: Event) => {
            // why error
            me._coreLogService.error("SOTWebSocketService.OpenConnection", "ws.onerror()", { "event": event });
          };
          me._ws.onopen = () => {/*ignore*/ }; // not need
          me._ws.onclose = (event: CloseEvent) => {
            me._coreLogService.debug("SOTWebSocketService.OpenConnection", "ws.onclose()", { "code": event.code, "reason": event.reason });
            me._ws = null;
            me._connectionStatusChanged.next(me.isConnected);
          };
          me._ws.onmessage = (event: any) => {
            if (event.data && typeof event.data == "string") {
              me.ReceiveMessage(event.data);
            } else {
              me._coreLogService.error("SOTWebSocketService.OpenConnection", "_ws.onmessage recieved wrong message", event);
              me.ReceiveMessage(null); // nothing
            }
          };
          me._connectionStatusChanged.next(me.isConnected);
          resolve(true);
        }
        else {
          resolve(false);
        }
      } catch (error) {
        me._coreLogService.error("SOTWebSocketService.OpenConnection", "new WebSocket", error);
        reject();
      }
    });
  }

  /** Open new Websocket and return it, else null */
  private GetWebSocket(): Promise<WebSocket> {
    const me = this;
    return new Promise<WebSocket>((resolve, reject) => {
      try {
        const ws = new WebSocket("ws://localhost:" + me.port);
        ws.onerror = (event: Event) => {
          // why error
          me._coreLogService.error("SOTWebSocketService.GetWebSocket", "ws.onerror()", { "event": event });
        };
        ws.onopen = (event: Event) => {
          //me._coreLogService.debug("SOTWebSocketService.GetWebSocket", "ws.onopen()", { "event": event });
          resolve(ws);
        };
        ws.onclose = (event: CloseEvent) => {
          //me._coreLogService.debug("SOTWebSocketService.GetWebSocket", "ws.onclose()", { "code": event.code, "reason": event.reason });
          resolve(null);
        };
      } catch (error) {
        me._coreLogService.error("SOTWebSocketService.GetWebSocket", "new WebSocket", error);
        reject();
      }
    });
  }

  /** wird aufgeruffen wenn daten über den WebSoked eingehen */
  private ReceiveMessage(message: string){
    const me = this;
    me._ngZone.run(async () => {
      //me._coreLogService.debug("SOTWebSocketService.ReciveMessage", "data received", {"message": message });
      me._messageReceived.next(message);
    });
  }

  private _messageReceived: Subject<string> = new Subject<string>();
  /** if Message received from SOT */
  public get messageReceived(): Observable<string> {
    return this._messageReceived.asObservable();
  }

  /** Sends Message to SOT */
  public Send(message: any) {
    this._ws.send(message);
  }
}
